/**
 * A slide-over for one record, opened from a row without losing the list.
 *
 * Modal semantics done by hand: `role="dialog"` + `aria-modal`, focus moved
 * into the panel on open and returned to the invoking element on close, Escape
 * closes, and Tab is cycled within the panel. A slide-over that leaks focus back
 * to the list behind it is worse than no slide-over — the keyboard user ends up
 * operating a table they cannot see.
 *
 * The panel is a *supplement* to the detail route, never a replacement: every
 * list that opens one also links to the full page, so a detail view stays
 * addressable, shareable and reloadable.
 */

import { useCallback, useEffect, useRef, type ReactNode } from 'react';

import { Button } from './Button';
import { CloseIcon } from './Icon';

const FOCUSABLE =
  'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

export function DetailPanel({
  open,
  title,
  subtitle,
  onClose,
  children,
  footer,
  width = '34rem',
}: {
  open: boolean;
  title: ReactNode;
  subtitle?: ReactNode;
  onClose: () => void;
  children: ReactNode;
  footer?: ReactNode;
  width?: string;
}) {
  const panelRef = useRef<HTMLDivElement | null>(null);
  const returnFocusTo = useRef<HTMLElement | null>(null);

  // Remember where focus came from, so closing puts it back rather than
  // dumping the user at the top of the document.
  useEffect(() => {
    if (!open) return;
    returnFocusTo.current = document.activeElement as HTMLElement | null;
    const first = panelRef.current?.querySelector<HTMLElement>(FOCUSABLE);
    (first ?? panelRef.current)?.focus();
    return () => returnFocusTo.current?.focus?.();
  }, [open]);

  const onKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLDivElement>) => {
      if (event.key === 'Escape') {
        event.stopPropagation();
        onClose();
        return;
      }
      if (event.key !== 'Tab') return;
      const nodes = panelRef.current?.querySelectorAll<HTMLElement>(FOCUSABLE);
      if (!nodes || nodes.length === 0) return;
      const first = nodes[0];
      const last = nodes[nodes.length - 1];
      if (!first || !last) return;
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    },
    [onClose],
  );

  if (!open) return null;

  return (
    <div className="detail-panel__layer">
      {/* Clicking away closes. Not focusable and hidden from AT — Escape and the
          close button are the accessible paths. */}
      <div className="detail-panel__scrim" onClick={onClose} aria-hidden="true" />
      <div
        ref={panelRef}
        className="detail-panel"
        style={{ width }}
        role="dialog"
        aria-modal="true"
        aria-label={typeof title === 'string' ? title : undefined}
        tabIndex={-1}
        onKeyDown={onKeyDown}
      >
        <header className="detail-panel__header">
          <div>
            <h2 className="detail-panel__title">{title}</h2>
            {subtitle ? <p className="detail-panel__subtitle">{subtitle}</p> : null}
          </div>
          <Button
            kind="ghost"
            size="sm"
            hasIconOnly
            renderIcon={CloseIcon}
            iconDescription="Close panel"
            onClick={onClose}
          />
        </header>
        <div className="detail-panel__body">{children}</div>
        {footer ? <footer className="detail-panel__footer">{footer}</footer> : null}
      </div>
    </div>
  );
}
